/**
 * User Details Modal Component
 * 
 * Displays comprehensive user information with admin tools
 */

import { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Mail, 
  Calendar, 
  Clock, 
  CreditCard,
  FolderOpen,
  Image,
  Shield,
  AlertTriangle,
  FileText,
  Plus,
  Crown
} from 'lucide-react';
import { adminApi } from '../stores/adminStore';

interface User {
  id: string;
  name: string;
  email?: string;  // Make email optional
  credits: number;
  status: 'active' | 'suspended' | 'banned';
  created_at: string;
  last_login_at?: string;
  total_albums?: number;  // Changed from album_count to match backend
  total_assets?: number;  // Changed from asset_count to match backend
  profile_image?: string;
  suspended_by?: string;
  banned_by?: string;
  ban_reason?: string;
  ban_expires_at?: string;
  storage_used_bytes?: number;  // Changed from storage_used to match backend
  // Subscription/Membership fields
  current_package?: string;
  membership_expires_at?: string;
  subscription_id?: string;
  subscription_status?: string;
  next_billing_date?: string;
  free_account_status?: boolean;
}

interface AdminNote {
  id: string;
  note: string;
  note_type: 'general' | 'warning' | 'violation' | 'appeal' | 'support';
  created_at: string;
  admin_users: {
    username: string;
  };
}

interface Props {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdate: () => void;
}

export default function UserDetailsModal({ user, isOpen, onClose, onUserUpdate }: Props) {
  
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState<User | null>(user || null);
  const [notes, setNotes] = useState<AdminNote[]>([]);
  const [creditHistory, setCreditHistory] = useState<any[]>([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'general' | 'warning' | 'violation' | 'appeal' | 'support'>('general');
  const [activeTab, setActiveTab] = useState<'overview' | 'notes' | 'credits'>('overview');
  const [actionLoading, setActionLoading] = useState(false);

  // Load user details, notes, and credit history
  const loadUserData = async () => {
    if (!isOpen || !user?.id) return;

    setLoading(true);
    try {
      const [userResponse, notesResponse, creditsResponse] = await Promise.allSettled([
        adminApi.get(`/admin/users/${user.id}`),
        adminApi.get(`/admin/users/${user.id}/notes?limit=20`),
        adminApi.get(`/admin/users/${user.id}/credits/history?limit=10`)
      ]);

      if (userResponse.status === 'fulfilled') {
        const responseData = userResponse.value.data.data || userResponse.value.data;
        const userData = responseData.user || responseData;
        if (userData) {
          setUserDetails(userData);
        }
      }

      if (notesResponse.status === 'fulfilled') {
  // ...existing code...
        const notesData = notesResponse.value.data.data || notesResponse.value.data;
        const notesList = notesData.notes || notesData || [];
  // ...existing code...
        setNotes(notesList);
      } else {
        console.error('Notes response failed:', notesResponse);
      }

      if (creditsResponse.status === 'fulfilled') {
        const creditsData = creditsResponse.value.data.data || creditsResponse.value.data;
        const transactionsList = creditsData.transactions || creditsData || [];
        setCreditHistory(transactionsList);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load data when modal opens
  useEffect(() => {
    if (user) {
      setUserDetails(user);
      loadUserData();
    }
  }, [isOpen, user?.id]);

  // Add admin note
  const addNote = async () => {
    if (!newNote.trim()) return;

    setActionLoading(true);
    try {
      await adminApi.post(`/admin/users/${user.id}/note`, {
        note: newNote.trim(),
        note_type: noteType
      });

      setNewNote('');
      setNoteType('general');

      await loadUserData(); // Reload to show new note

      alert('✅ Note added successfully!');
    } catch (error: any) {
      console.error('Failed to add note:', error);
      alert('Failed to add note: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Update user status
  const updateUserStatus = async (status: string, reason?: string, duration?: number) => {
    setActionLoading(true);
    try {
      const data: any = { status };
      if (reason) data.reason = reason;
      if (duration && duration > 0) data.duration = duration;

      await adminApi.put(`/admin/users/${user.id}/status`, data);
      await loadUserData();
      onUserUpdate();
    } catch (error: any) {
      console.error(`Failed to ${status} user:`, error);
      alert(`Failed to ${status} user: ` + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Delete user permanently
  const deleteUser = async (reason: string) => {
    setActionLoading(true);
    try {
      await adminApi.delete(`/admin/users/${user?.id}`, {
        data: {
          reason: reason,
          confirmDelete: true
        }
      });
      
      alert(`✅ User '${user?.name}' has been permanently deleted.`);
      onClose();
      onUserUpdate(); // Refresh user list
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      alert(`❌ Failed to delete user: ` + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getNoteTypeColor = (type: string) => {
    const colors = {
      general: 'bg-gray-100 text-gray-800',
      warning: 'bg-yellow-100 text-yellow-800',
      violation: 'bg-red-100 text-red-800',
      appeal: 'bg-blue-100 text-blue-800',
      support: 'bg-green-100 text-green-800'
    };
    return colors[type as keyof typeof colors] || colors.general;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12">
              {userDetails?.profile_image ? (
                <img 
                  className="w-12 h-12 rounded-full object-cover" 
                  src={userDetails.profile_image} 
                  alt={userDetails.name || 'User'}
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                  <User className="w-6 h-6 text-gray-600" />
                </div>
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{userDetails?.name || 'Loading...'}</h2>
              <p className="text-gray-500">{userDetails?.email || 'No email'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'overview' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'notes' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Admin Notes ({notes.length})
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'credits' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Credit History
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* User Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
                      
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Mail className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Email</p>
                            <p className="font-medium">{userDetails?.email || 'No email'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Joined</p>
                            <p className="font-medium">{formatDate(userDetails?.created_at || '')}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Clock className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Last Login</p>
                            <p className="font-medium">{formatDate(userDetails?.last_login_at || '')}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <CreditCard className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Credits</p>
                            <p className="font-bold text-lg text-green-600">{userDetails?.credits?.toLocaleString() || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">Activity Stats</h3>
                      
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <FolderOpen className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Albums Created</p>
                            <p className="font-medium">{userDetails?.total_albums || 0}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Image className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Images Uploaded</p>
                            <p className="font-medium">{userDetails?.total_assets || 0}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <Shield className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Account Status</p>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              userDetails?.status === 'active' ? 'bg-green-100 text-green-800' :
                              userDetails?.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {userDetails?.status?.toUpperCase() || 'UNKNOWN'}
                            </span>
                          </div>
                        </div>

                        {userDetails?.storage_used_bytes && (
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-sm text-gray-500">Storage Used</p>
                              <p className="font-medium">{formatBytes(userDetails.storage_used_bytes)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Membership/Subscription Info */}
                  {(userDetails?.current_package || userDetails?.subscription_id) && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Crown className="w-5 h-5 text-purple-600" />
                        <h4 className="font-semibold text-purple-900">Membership & Subscription</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {userDetails?.current_package && (
                          <div>
                            <p className="text-sm text-purple-600 font-medium">Current Package</p>
                            <p className="text-purple-900 font-semibold">{userDetails.current_package}</p>
                          </div>
                        )}
                        {userDetails?.subscription_status && (
                          <div>
                            <p className="text-sm text-purple-600 font-medium">Subscription Status</p>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              userDetails.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                              userDetails.subscription_status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              userDetails.subscription_status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {userDetails.subscription_status.toUpperCase()}
                            </span>
                          </div>
                        )}
                        {userDetails?.membership_expires_at && (
                          <div>
                            <p className="text-sm text-purple-600 font-medium">Membership Expires</p>
                            <p className="text-purple-900">{formatDate(userDetails.membership_expires_at)}</p>
                          </div>
                        )}
                        {userDetails?.next_billing_date && (
                          <div>
                            <p className="text-sm text-purple-600 font-medium">Next Billing Date</p>
                            <p className="text-purple-900">{formatDate(userDetails.next_billing_date)}</p>
                          </div>
                        )}
                        {userDetails?.subscription_id && (
                          <div className="md:col-span-2">
                            <p className="text-sm text-purple-600 font-medium">Subscription ID</p>
                            <p className="text-purple-900 font-mono text-sm">{userDetails.subscription_id}</p>
                          </div>
                        )}
                        {userDetails?.free_account_status !== undefined && (
                          <div>
                            <p className="text-sm text-purple-600 font-medium">Account Type</p>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              userDetails.free_account_status ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                            }`}>
                              {userDetails.free_account_status ? 'FREE' : 'PAID'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Status Info */}
                  {(userDetails?.status === 'suspended' || userDetails?.status === 'banned') && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <h4 className="font-semibold text-red-900">Account Restricted</h4>
                      </div>
                      {userDetails?.ban_reason && (
                        <p className="text-red-700 mb-2">
                          <strong>Reason:</strong> {userDetails.ban_reason}
                        </p>
                      )}
                      {userDetails?.ban_expires_at && (
                        <p className="text-red-700">
                          <strong>Expires:</strong> {formatDate(userDetails.ban_expires_at)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Admin Actions */}
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Actions</h3>
                    <div className="flex flex-wrap gap-3">
                      {userDetails?.status === 'active' && (
                        <>
                          <button
                            onClick={() => {
                              const reason = prompt('Reason for suspension:');
                              if (reason) updateUserStatus('suspended', reason);
                            }}
                            disabled={actionLoading}
                            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                          >
                            Suspend User
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt('Reason for deletion (required):');
                              if (reason && reason.trim()) {
                                const confirm = window.confirm(
                                  `⚠️ PERMANENT DELETE\n\nThis will PERMANENTLY delete:\n• User account\n• All albums & images\n• All data (except transactions)\n\nTHIS CANNOT BE UNDONE!\n\nType 'DELETE' to confirm:`
                                );
                                if (confirm && prompt('Type DELETE to confirm:') === 'DELETE') {
                                  deleteUser(reason.trim());
                                }
                              }
                            }}
                            disabled={actionLoading}
                            className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50 border-2 border-red-600"
                          >
                            🗑️ Delete User
                          </button>
                        </>
                      )}

                      {userDetails?.status === 'suspended' && (
                        <button
                          onClick={() => updateUserStatus('active')}
                          disabled={actionLoading}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          Unsuspend User
                        </button>
                      )}

                      {userDetails?.status === 'banned' && (
                        <button
                          onClick={() => updateUserStatus('active')}
                          disabled={actionLoading}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          Unban User
                        </button>
                      )}

                      {actionLoading && (
                        <div className="flex items-center gap-2 text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                          Processing...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="space-y-6">
                  {/* Add Note */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Admin Note</h3>
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <select
                          value={noteType}
                          onChange={(e) => setNoteType(e.target.value as any)}
                          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="general">General</option>
                          <option value="warning">Warning</option>
                          <option value="violation">Violation</option>
                          <option value="appeal">Appeal</option>
                          <option value="support">Support</option>
                        </select>
                      </div>
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add internal admin note..."
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={addNote}
                        disabled={!newNote.trim() || actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-4 h-4" />
                        Add Note
                      </button>
                    </div>
                  </div>

                  {/* Notes List */}
                  <div className="space-y-4">
                    {notes.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No admin notes yet.</p>
                    ) : (
                      notes.map((note) => (
                        <div key={note.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getNoteTypeColor(note.note_type)}`}>
                              {note.note_type.toUpperCase()}
                            </span>
                            <div className="text-sm text-gray-500">
                              <p>{note.admin_users?.username || 'Admin'}</p>
                              <p>{formatDate(note.created_at)}</p>
                            </div>
                          </div>
                          <p className="text-gray-900">{note.note}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Credit History Tab */}
              {activeTab === 'credits' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Credit Transaction History</h3>
                    <div className="text-lg font-bold text-green-600">
                      Current: {userDetails?.credits?.toLocaleString() || 0} credits
                    </div>
                  </div>

                  {creditHistory.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No credit transactions yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {creditHistory.map((transaction: any) => {
                        const isCredit = transaction.type === 'purchase' || transaction.type === 'admin_add';
                        const isSub = transaction.type === 'usage' || transaction.type === 'deduct';
                        
                        return (
                          <div key={transaction.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">
                                    {transaction.package_name || transaction.type?.toUpperCase() || 'Transaction'}
                                  </p>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {transaction.status}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{formatDate(transaction.created_at)}</p>
                                {transaction.payment_method && (
                                  <p className="text-xs text-gray-400 mt-1 font-mono">
                                    via {transaction.payment_method.toUpperCase()}
                                    {transaction.paypal_capture_id && ` • ${transaction.paypal_capture_id}`}
                                    {!transaction.paypal_capture_id && transaction.paypal_order_id && ` • ${transaction.paypal_order_id}`}
                                  </p>
                                )}
                              </div>
                              <div className="text-right ml-4">
                                <div className={`text-lg font-bold ${
                                  isCredit ? 'text-green-600' : isSub ? 'text-red-600' : 'text-gray-600'
                                }`}>
                                  {isCredit ? '+' : isSub ? '-' : ''}{transaction.amount}
                                  <span className="text-sm font-normal text-gray-500"> credits</span>
                                </div>
                                {transaction.price && (
                                  <p className="text-sm text-gray-500">
                                    ${transaction.price.toFixed(2)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}